import React, { Fragment, useEffect, useState } from 'react';
import { Button, Row, Col, Form } from 'react-bootstrap';

import { requestList as request } from 'api/standard/code/mix';
import ActionButton from 'components/common/ActionButton';
import CheckPlant from 'components/common-sfims/CheckPlant';
import Select from 'components/common-sfims/form/Select';
import ListInContent from 'components/common-sfims/ListInContent';
import columns from 'components/pages/standard/code/mix/columns';
import Setting from 'components/pages/standard/code/mix/Setting';
import Upload from 'components/pages/standard/code/mix/Upload';
import { array2ValueAndText, datas2ExcelJSON, utc2tz } from 'function/convert';
import { downloadExcelByJSON } from 'function/file';
import { getText } from 'function/language';
import useCheckPlantState from 'global/checkPlantState';
import useListScroll from 'hooks/useListScroll';
import useObjReducer from 'hooks/useObjReducer';
import { RETURN_YN, RETURN_YN_SELECT } from 'types/type';
import { RESPONSE_CODE } from 'types/const';
import pluck from 'utils/array/pluck';
import comma from 'utils/format/comma';
import format from 'utils/string/format';

/**
 * 기준 정보 > 배합비 코드 - 목록
 */
const List = () => {
  /* hook */
  const { end, setEnd, total, setTotal } = useListScroll();

  /* recoil */
  const { checkPlant } = useCheckPlantState();

  /* state */
  // 목록
  const [datas, setDatas] = useState([]);
  // 로딩 여부
  const [isLoading, setIsLoading] = useState(true);
  // 설정 모달 표시 여부
  const [isShowSetting, setIsShowSetting] = useState(false);
  // 업로드 모달 표시 여부
  const [isShowUpload, setIsShowUpload] = useState(false);
  // 메시지
  const [message, setMessage] = useState({});
  // 배합비 코드 검색어
  const [mixCd, setMixCd] = useState('');
  // 배합비 코드 목록 조회 요청 데이터
  const [params, setParams] = useObjReducer({ factoryCodeSeqList: checkPlant });
  // 선택한 번호
  const [selectSeq, setSelectSeq] = useState(0);
  // 선택한 사용 유무
  const [useYn, setUseYn] = useState();

  /* watch */
  useEffect(() => {
    requestList();
  }, [params]);

  /* method */
  // 배합비 코드 목록 조회 요청
  const requestList = async () => {
    setEnd(20);
    setTotal(0);
    setDatas([]);
    setIsLoading(true);
    setMessage({});

    try {
      const {
        list = [],
        totalCount = 0,
        code,
        message
      } = await request(params);

      if (code !== RESPONSE_CODE.Success) {
        throw {
          code,
          error: message
        };
      }

      setTotal(totalCount);
      setDatas(
        list.map((value = {}, index) => ({
          ...value,
          rowNum: totalCount - index,
          regDt: utc2tz(value.regDt)
        }))
      );
    } catch (error) {
      setMessage(error);
    } finally {
      setIsLoading(false);
    }
  };
  // 설정 모달 표시
  const showSetting = seq => {
    setIsShowSetting(true);
    setSelectSeq(seq);
  };

  /* event */
  // 엑셀 다운로드 클릭 시
  const onDownloadExcelClick = () => {
    downloadExcelByJSON(
      datas2ExcelJSON(
        datas.map((value = {}) => ({
          ...value,
          productInMixList: pluck(value.productInMixList, 'prdtNm').join(', ')
        })),
        columns
          .filter(key => key !== 'update')
          .reduce(
            (result, key) => ({
              ...result,
              [key]: {}
            }),
            {}
          )
      ),
      `${getText('mixCd')}.xlsx`
    );
  };
  // 검색 클릭 시
  const onSearchClick = event => {
    event.preventDefault();

    setParams({
      factoryCodeSeqList: checkPlant,
      mixCd,
      useYn
    });
  };

  /* view */
  return (
    <>
      <ListInContent
        columns={columns}
        message={message}
        showLoading={isLoading}
      >
        <Fragment key="headerButton">
          <Button size="sm" onClick={() => setIsShowUpload(true)}>
            {getText('uploadExcel')}
          </Button>
          <Button size="sm" className="ms-2" onClick={() => showSetting(0)}>
            + {getText('insert')}
          </Button>
        </Fragment>
        <Fragment key="search">
          <CheckPlant />
          <Form onSubmit={onSearchClick}>
            <Row className="ms-0 me-0 mt-3">
              <Col sm={1} className="ps-1 pe-1">
                <Form.Control
                  size="sm"
                  type="text"
                  value={mixCd}
                  autoFocus
                  placeholder={getText('mixCd')}
                  onChange={({ target: { value } }) => setMixCd(value)}
                />
              </Col>
              <Col sm={1} className="ps-1 pe-1">
                <Select
                  datas={array2ValueAndText(RETURN_YN_SELECT, 'key', 'value')}
                  placeholder="useYn"
                  showAll
                  value={useYn}
                  className="w-100"
                  onChange={({ target: { value } }) => setUseYn(value)}
                />
              </Col>
              <Col sm={10} className="ps-1 pe-1 d-flex align-items-center">
                <Button disabled={isLoading} size="sm" type="submit">
                  {getText('search')}
                </Button>
                <p className="mb-0 ms-3 fs--1 fw-bold lh-1">
                  {format(getText('total'), { total: comma(total) })}
                </p>
                {datas.length > 0 && (
                  <Button
                    size="sm"
                    variant="secondary"
                    className="ms-2"
                    onClick={onDownloadExcelClick}
                  >
                    {getText('downloadExcel')}
                  </Button>
                )}
              </Col>
            </Row>
          </Form>
        </Fragment>
        <Fragment key="datas">
          {datas.slice(0, end).map((value = {}) => (
            <tr
              key={`datas${value.seq}`}
              className={
                value.useYn === RETURN_YN.Y ? 'bg-soft-success' : 'bg-200'
              }
            >
              {columns.map(key => (
                <td key={`datas${value.seq}.${key}`}>
                  {key === 'productInMixList' ? (
                    value.productInMixList?.map?.(
                      ({ prdtNm, useYn, prdtCd } = {}, index) => (
                        <Fragment key={`productInMixList.${prdtCd}`}>
                          {index > 0 && ', '}
                          {useYn === RETURN_YN.Y ? (
                            <span className="text-danger">{prdtNm}</span>
                          ) : (
                            prdtNm
                          )}
                        </Fragment>
                      )
                    )
                  ) : key === 'update' ? (
                    <ActionButton
                      title={getText('update')}
                      icon="edit"
                      variant="action"
                      className="p-0 lh-1"
                      onClick={() => showSetting(value.seq)}
                    />
                  ) : (
                    value[key]
                  )}
                </td>
              ))}
            </tr>
          ))}
        </Fragment>
      </ListInContent>
      <Upload
        show={isShowUpload}
        onHide={() => setIsShowUpload(false)}
        onUpload={requestList}
      />
      <Setting
        show={isShowSetting}
        seq={selectSeq}
        onHide={() => setIsShowSetting(false)}
        onSetting={requestList}
      />
    </>
  );
};

export default List;
